/**
 * Client-side authorization hooks.
 *
 * Wraps Clerk's `useOrganization()` so individual components don't
 * sprinkle role-string comparisons everywhere. Each `useCan…()` hook
 * exposes a single boolean — easy to compose into `&&`-gated UI and
 * easy to extend when Phase 3 adds new mutation surfaces.
 *
 * Role keys mirror the Worker side (`worker/lib/auth.ts`):
 *   - `org:owner`
 *   - `org:admin`        (Clerk built-in; treated as owner-equivalent)
 *   - `org:mechanic`
 *   - `org:receptionist`
 *
 * Authorization is enforced on the Worker as well — these hooks only
 * govern UI visibility / interactivity. A receptionist with the dev
 * console open can't bypass the `requireRole(...)` middleware just
 * by un-hiding the Approve button.
 */

import { useOrganization, useUser } from '@clerk/clerk-react'

export const ROLE_OWNER = 'org:owner'
export const ROLE_ADMIN = 'org:admin'
export const ROLE_MECHANIC = 'org:mechanic'
export const ROLE_RECEPTIONIST = 'org:receptionist'

export type UserRole =
  | typeof ROLE_OWNER
  | typeof ROLE_ADMIN
  | typeof ROLE_MECHANIC
  | typeof ROLE_RECEPTIONIST

/**
 * Returns the active user's role within the active organization, or
 * `null` if not loaded yet / no active org / no membership record /
 * unknown role.
 *
 * Reads from `useOrganization().membership.role` — Clerk's source of
 * truth for the current session's org role. Stays in sync with the
 * `<OrganizationSwitcher />` automatically.
 */
export function useUserRole(): UserRole | null {
  const { membership, isLoaded } = useOrganization()
  if (!isLoaded) return null
  const role = membership?.role
  if (
    role === ROLE_OWNER ||
    role === ROLE_ADMIN ||
    role === ROLE_MECHANIC ||
    role === ROLE_RECEPTIONIST
  ) {
    return role
  }
  return null
}

/** Owners (incl. admins) and mechanics can approve / modify / decline estimates. */
export function useCanApproveEstimate(): boolean {
  const role = useUserRole()
  return (
    role === ROLE_OWNER || role === ROLE_ADMIN || role === ROLE_MECHANIC
  )
}

/** Owners (incl. admins) only — third-party API keys, billing, partner integrations. */
export function useCanManageIntegrations(): boolean {
  const role = useUserRole()
  return role === ROLE_OWNER || role === ROLE_ADMIN
}

/** Owners (incl. admins) only — invite / remove org members, change roles. */
export function useCanManageTeam(): boolean {
  const role = useUserRole()
  return role === ROLE_OWNER || role === ROLE_ADMIN
}

/** Owners (incl. admins) only — create / rename / recolor / delete shop tag dictionary entries. */
export function useCanManageTags(): boolean {
  const role = useUserRole()
  return role === ROLE_OWNER || role === ROLE_ADMIN
}

/**
 * Owners (incl. admins) only — permanently delete a customer or vehicle
 * record. Destructive + irreversible (recoverable only via the admin
 * Data Safety restore), so it sits alongside the other owner-gated
 * mutations. The Worker enforces the same gate via
 * `requireRole(...OWNER_ROLES)` on the DELETE routes — this only governs
 * button visibility.
 */
export function useCanDeleteRecords(): boolean {
  const role = useUserRole()
  return role === ROLE_OWNER || role === ROLE_ADMIN
}

/**
 * Returns `true` if the active user's Clerk publicMetadata has
 * `platform_admin: true`. Distinct from org-level roles
 * (owner/admin/mechanic/receptionist): platform_admin gates the
 * /admin/* surface for autoflo operators (Mira + Emil), not for
 * tenant shop owners.
 *
 * Reads from `useUser().user.publicMetadata.platform_admin`. The
 * Worker enforces the same gate via the `requirePlatformAdmin`
 * middleware, so a tenant who tampers with localStorage / dev tools
 * can't reach platform-admin routes by un-hiding the nav link.
 *
 * Returns `false` while Clerk is still loading — fail-closed default.
 */
export function useIsPlatformAdmin(): boolean {
  const { user, isLoaded } = useUser()
  if (!isLoaded) return false
  const md = user?.publicMetadata as Record<string, unknown> | undefined
  return md?.platform_admin === true
}

/**
 * Per-user "Home v2" preference.
 *
 * Stored on Clerk `unsafeMetadata.home_v2` (client-writable, synced
 * across the user's devices, no DB migration / RLS). **Pre-enabled:**
 * Home v2 is the default home — `enabled` is true unless the user has
 * explicitly opted out (`home_v2 === false`) via the Settings switch.
 * Fail-open to the new default while Clerk loads (cosmetic preference;
 * the Dashboard mounts post-OrgGate, so a pre-load frame is unlikely).
 *
 * `setEnabled` does a read-merge-write — Clerk metadata writes REPLACE
 * the whole object (see CLAUDE.md "Metadata writes replace, not
 * merge"), so we spread the existing keys to avoid clobbering them.
 */
export function useHomeV2Preview(): {
  enabled: boolean
  isLoaded: boolean
  setEnabled: (next: boolean) => Promise<void>
} {
  const { user, isLoaded } = useUser()
  const md = (user?.unsafeMetadata ?? {}) as Record<string, unknown>
  const enabled = md.home_v2 !== false
  const setEnabled = async (next: boolean) => {
    if (!user) return
    const existing = (user.unsafeMetadata ?? {}) as Record<string, unknown>
    await user.update({ unsafeMetadata: { ...existing, home_v2: next } })
  }
  return { enabled, isLoaded, setEnabled }
}
